import {EventEmitter, Injectable} from '@angular/core';
import * as Rx from 'rxjs/Rx';
import {isFunction, isNull, isUndefined} from 'util';
import {Http, RequestOptionsArgs, Response, Headers} from '@angular/http';
import {environment} from '../../environments/environment';
import {HttpService} from '../core/services/http.service';
import * as JwtDecode from 'jwt-decode';
import {DTO, User} from '../core/entities/entities';
import {Observable} from 'rxjs/Observable';
import {ErrorService, ErrorType} from '../core/services/error.service';
import {ServiceName} from '../core/services/Service';
import * as lodash from 'lodash';


@Injectable()
export class AuthService {

  private _user: User;
  private _profiles: string[];
  private _currentProfile: string;
  private _tokenName = environment.authToken;
  private _logoutEvent = new EventEmitter<User>();
  private _loginEvent = new EventEmitter<User>();
  private _routeEvent = new EventEmitter();
  public readonly logoutEvent: Observable<User> = this._logoutEvent.asObservable();
  public readonly loginEvent: Observable<User> = this._loginEvent.asObservable();
  public readonly routeChangeEvent: Observable<any> = this._routeEvent.asObservable();
  private _defHeaders = new Headers(
    {
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET, POST, OPTIONS, PUT, PATCH, DELETE',
      'Access-Control-Allow-Headers': 'X-Requested-With,content-type',
      'Access-Control-Allow-Credentials': true,
      'context': 'User'
     /* 'Content-Type': 'application/json'*/
    });

  get loggedIn(): boolean {
    return !isUndefined(this._user) && !isNull(this._user) && !isNull(this._user.userId);
  }

  private role2Profile(role: string): string {

    return role;
    // return 'hostess,nurse,cashier,patient,medic';
  }


  get currentProfile(): string {
    if (this._profiles && this._profiles.length) {
      if (!this._currentProfile) {
        this._currentProfile = this._profiles[0];
      }
      return this._currentProfile;
    }
    return null;
  }


  hasProfile(profile: string): boolean {
    return this._profiles.includes(profile);
  }

  setRouteChange(){
    this._routeEvent.emit({});
  }

  isLoggedIn(): Rx.Observable<boolean> {


    if (isUndefined(this._user)) {
      const authToken = localStorage.getItem(this._tokenName);
      if (authToken) {
        // fixme implementare servizio che mi consenta di testare l'utente connesso...
        this.assignUser(authToken);
      }
    }

    return Rx.Observable.from([this.loggedIn]);
  }

  get user(): User {
    return Object.assign({}, this._user);
  }

  getUser(): User {
    return this.user;
  }

  // Takes the raw JWT string. The backend login/verify now returns the lib JwtResponse ({token,type}),
  // so callers pass `body.token` (was the old {data:{token}} envelope). isLoggedIn passes the stored token.
  private assignUser(token: string, profile?: string, roles?: string []) {
    this._user = <User>JwtDecode(token);

    this._user.profile = this.role2Profile(this._user.role);

    localStorage.setItem(this._tokenName, token);

    this._profiles =  roles || lodash.sortedUniq(this.user.profile.split(',').map( v => v.toLowerCase().replace('role_','')));
    const options: RequestOptionsArgs = {};
    const headers = Object.assign(new Headers({}), this._defHeaders);
    headers.set(environment.authToken, 'Bearer ' + token);
    options.headers = headers;
    this._httpService.setOptions(options);
    this._loginEvent.emit(this.user);
  }

  private eraseUser(data?) {
    const u = this.user;
    this._user = null;
    this._profiles = [];
    this._currentProfile = null;

    localStorage.removeItem(this._tokenName);
    this._httpService.deleteOptions();
    this._httpService.setOptions({headers: this._defHeaders});
    this._logoutEvent.emit(u);
  }

  constructor(private _httpService: HttpService, private _errorService: ErrorService) {
    this._defHeaders.set(environment.apiKey, environment.apiKeyValue);
    this._httpService.setOptions({headers: this._defHeaders});
  }

  public getError(dto: DTO) {
    const body = dto;
    if (!body || !body.success) {
      if (body) {
        const error = new Error();
        error.name = body.errorCode;
        error.message = body.errorMessage;
        return error;
      }
    }

    return null;
  }

  public handleError(error: any, type?: ErrorType) {
    if (error && error.json && isFunction(error.json)) {
      const body: DTO = <DTO> error.json();
      const err = this.getError(body);
      if (err) {
        type = ErrorType.AUTH;
        error = err;
      }
    }

    this._errorService.push(error, type);
    return Observable.throw(error);
  }

  // Resolves to either a logged-in user (no 2FA, or after verify) or a 2FA challenge that the caller
  // must complete via verify2FA(). The backend (shared LoginController under /v1) returns the lib
  // JwtResponse: 201 { token, type } on a final login, 202 { tfaenabled, tfatype, challengeToken } when
  // a second factor is required. Errors arrive as HTTP 401 -> catch (no more {success:false} envelope).
  login(credentials: { userid: string, pwd: string, profile?: string, roles?: string[] }):
      Rx.Observable<{ user?: User, challengeToken?: string, tfaType?: string }> {
    const me = this;

    return this._httpService.post(environment.apiUrl + 'login', {
      username: credentials.userid,
      password: credentials.pwd
    }, {withCredentials: true}).map(function (res: Response) {

      if (res.status === 202) {
        // Second factor required: hand the challenge token + type back to the login screen.
        const challenge: any = res.json();
        return { challengeToken: challenge.challengeToken, tfaType: challenge.tfatype };
      }
      // 201: final token, no 2FA.
      const body: any = res.json();
      me.assignUser(body.token, credentials.profile, credentials.roles);
      return { user: me.user };
    }).catch(err => me.handleError(err));
  }

  // Second factor: submit the challenge token from login()'s 202 plus the user's code to /api/login/2FA.
  // 201 { token } -> full session; a wrong/expired code is a 401 -> catch (surfaced to the caller).
  verify2FA(challengeToken: string, code: string): Rx.Observable<User> {
    const me = this;

    return this._httpService.post(environment.apiUrl + 'login/2FA', {
      challengeToken: challengeToken,
      code: code
    }, {withCredentials: true}).map(function (res: Response) {
      const body: any = res.json();
      me.assignUser(body.token);
      return me.user;
    }).catch(err => me.handleError(err));
  }

  logout(): Rx.Observable<boolean> {
    this.eraseUser();
    return Rx.Observable.from([true]);
  }

  token() {
    return localStorage.getItem(this._tokenName);
  }

  refreshToken(): Rx.Observable<boolean> {
    const me = this;
    return this._httpService.post(environment.refreshUrl, null, {withCredentials: true})
      .map((res: Response) => {
        const body: any = res.json();
        const token = body && body.token;
        if (!token) {
          return false;
        }
        me.assignUser(token);
        return true;
      })
      .catch(() => Rx.Observable.of(false));
  }
}

ServiceName.add('AuthService', AuthService);



// WEBPACK FOOTER //
// ./src/app/auth/auth.service.ts